/**
 * Pointer-based drag-to-reorder list — works with a mouse AND a finger, which
 * the photo-configuration page's HTML5 drag events do not (they never fire on
 * touch; Ian runs the settings page from his iPhone). Pointer events unify
 * both: the handle captures the pointer, rows live-reorder as the pointer
 * crosses their midpoints, and the new order is committed on release only if
 * it changed. Near-edge auto-scroll keeps long ladders draggable on a phone,
 * where the page cannot be scrolled mid-drag (the handle sets
 * touch-action: none — required, or the browser steals the gesture to pan).
 */
import React, { useEffect, useRef, useState } from "react"
import { GripVertical } from "lucide-react"

import { THEME } from "../../config/theme"

/** Pixels from the viewport edge inside which dragging auto-scrolls. */
const AUTO_SCROLL_ZONE = 90
const AUTO_SCROLL_STEP = 12

export interface DragHandleProps {
  onPointerDown: (e: React.PointerEvent) => void
  style: React.CSSProperties
  "aria-label": string
}

interface Props<T> {
  items: T[]
  getKey: (item: T) => number | string
  /** Fired on release when the order actually changed — the full key list,
   * top first (the reorder endpoints' contract). */
  onReorder: (orderedKeys: Array<number | string>) => void
  disabled?: boolean
  /** Renders one row's content; spread handleProps onto the drag handle
   * element (or render <DragHandle {...handleProps} /> for the default). */
  renderItem: (item: T, handleProps: DragHandleProps, isDragging: boolean) => React.ReactNode
}

export function DragHandle(props: DragHandleProps) {
  return (
    <span {...props}>
      <GripVertical size={15} strokeWidth={2} />
    </span>
  )
}

export default function DragReorderList<T>({
  items,
  getKey,
  onReorder,
  disabled = false,
  renderItem,
}: Props<T>) {
  // Draft order lives as keys so parent updates (renames, color edits) flow
  // through while a drag is idle; the item list itself stays parent-owned.
  const [draftKeys, setDraftKeys] = useState<Array<number | string>>(() => items.map(getKey))
  const [draggingKey, setDraggingKey] = useState<number | string | null>(null)
  const rowRefs = useRef(new Map<number | string, HTMLDivElement>())
  const dragState = useRef<{ key: number | string; startOrder: Array<number | string> } | null>(null)

  // Resync from the parent whenever its row set/order changes while idle.
  const parentKeys = items.map(getKey)
  const parentSignature = parentKeys.join("|")
  useEffect(() => {
    if (!dragState.current) setDraftKeys(parentKeys)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentSignature])

  const byKey = new Map(items.map((item) => [getKey(item), item]))
  const orderedItems = draftKeys
    .map((key) => byKey.get(key))
    .filter((item): item is T => item !== undefined)

  const handlePointerDown = (key: number | string) => (e: React.PointerEvent) => {
    if (disabled) return
    e.preventDefault()
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    dragState.current = { key, startOrder: draftKeys }
    setDraggingKey(key)
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    const state = dragState.current
    if (!state) return

    // Keep long ladders draggable: nudge the page when near an edge.
    if (e.clientY < AUTO_SCROLL_ZONE) window.scrollBy(0, -AUTO_SCROLL_STEP)
    else if (e.clientY > window.innerHeight - AUTO_SCROLL_ZONE) window.scrollBy(0, AUTO_SCROLL_STEP)

    const currentIndex = draftKeys.indexOf(state.key)
    const targetIndex = targetIndexForPointer(
      e.clientY,
      draftKeys.map((key) => {
        const el = rowRefs.current.get(key)
        return el ? el.getBoundingClientRect() : null
      }),
      currentIndex,
    )
    if (targetIndex !== currentIndex) {
      const next = [...draftKeys]
      next.splice(currentIndex, 1)
      next.splice(targetIndex, 0, state.key)
      setDraftKeys(next)
    }
  }

  const handlePointerEnd = () => {
    const state = dragState.current
    if (!state) return
    dragState.current = null
    setDraggingKey(null)
    if (draftKeys.join("|") !== state.startOrder.join("|")) {
      onReorder(draftKeys)
    }
  }

  return (
    <div onPointerMove={handlePointerMove} onPointerUp={handlePointerEnd} onPointerCancel={handlePointerEnd}>
      {orderedItems.map((item) => {
        const key = getKey(item)
        const isDragging = key === draggingKey
        return (
          <div
            key={key}
            ref={(el) => {
              if (el) rowRefs.current.set(key, el)
              else rowRefs.current.delete(key)
            }}
            style={isDragging ? draggingRowStyle : undefined}
          >
            {renderItem(
              item,
              {
                onPointerDown: handlePointerDown(key),
                style: handleStyle(disabled),
                "aria-label": "Drag to reorder",
              },
              isDragging,
            )}
          </div>
        )
      })}
    </div>
  )
}

/** Which slot the pointer is over — pure so it can be unit-tested (jsdom has
 * no layout, so the component itself can't measure real rects in tests). */
export function targetIndexForPointer(
  pointerY: number,
  rowRects: Array<{ top: number; height: number } | null>,
  currentIndex: number,
): number {
  let target = currentIndex
  for (let i = 0; i < rowRects.length; i++) {
    const rect = rowRects[i]
    if (!rect) continue
    const midpoint = rect.top + rect.height / 2
    if (i < currentIndex && pointerY < midpoint) return i
    if (i > currentIndex && pointerY > midpoint) target = i
  }
  return target
}

const handleStyle = (disabled: boolean): React.CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  alignSelf: "stretch",
  padding: "2px 4px",
  color: THEME.colors.neutral.light.textSecondary,
  cursor: disabled ? "default" : "grab",
  // Without this the browser claims the touch gesture for scrolling and the
  // drag never starts on a phone.
  touchAction: "none",
})

const draggingRowStyle: React.CSSProperties = {
  opacity: 0.85,
  background: THEME.colors.lightGray,
  borderRadius: THEME.borderRadius.sm,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
  position: "relative",
  zIndex: 1,
}
